/**
 * CALIBRA Brand JS
 * Shared interactive logic for all Calibra sections.
 * - Vanilla JS, no dependencies.
 * - Idempotent: safe if any section element is absent.
 * - Deferred via defer attribute on <script> tag.
 * - Initialised once via a flag on window to prevent double-run.
 */
(function () {
  'use strict';

  if (window.__calibraInit) return;
  window.__calibraInit = true;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Flavour palette for showcase blob + body background ---- */
  var FLAVOUR_BLOB = [
    'radial-gradient(ellipse at 45% 45%, #BFE3C6 0%, rgba(191,227,198,0.4) 55%, transparent 100%)',
    'radial-gradient(ellipse at 45% 45%, #F6C9C4 0%, rgba(246,201,196,0.4) 55%, transparent 100%)',
    'radial-gradient(ellipse at 45% 45%, #F6DFA6 0%, rgba(246,223,166,0.4) 55%, transparent 100%)',
    'radial-gradient(ellipse at 45% 45%, #E7CDA8 0%, rgba(231,205,168,0.4) 55%, transparent 100%)'
  ];

  var FLAVOUR_BG = [
    'rgba(191,227,198,0.05)',
    'rgba(246,201,196,0.05)',
    'rgba(246,223,166,0.05)',
    'rgba(231,205,168,0.05)'
  ];

  /* ---- Utilities ---- */
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }

  function blendOverWhite(rgba) {
    var m = rgba.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/);
    if (!m) return '#ffffff';
    var r = parseInt(m[1], 10);
    var g = parseInt(m[2], 10);
    var b = parseInt(m[3], 10);
    var a = parseFloat(m[4]);
    var R = Math.round(255 * (1 - a) + r * a);
    var G = Math.round(255 * (1 - a) + g * a);
    var B = Math.round(255 * (1 - a) + b * a);
    return 'rgb(' + R + ',' + G + ',' + B + ')';
  }

  /* ---- IntersectionObserver reveals ---- */
  function initReveals() {
    var items = document.querySelectorAll('.calibra-reveal');
    if (!items.length) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('calibra-visible');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -48px 0px' });

    items.forEach(function (el) { obs.observe(el); });

    /* Also trigger .calibra-way reveal for the photo zoom */
    var ways = document.querySelectorAll('.calibra-way');
    var wayObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('calibra-visible');
          wayObs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    ways.forEach(function (el) { wayObs.observe(el); });
  }

  /* ---- Ticker: duplicate inner HTML for seamless infinite loop ---- */
  function initTicker() {
    var track = document.querySelector('.calibra-ticker__track');
    if (!track) return;
    /* Guard against double-init in theme editor */
    if (track.dataset.calibraDuped) return;
    track.dataset.calibraDuped = '1';
    track.innerHTML = track.innerHTML + track.innerHTML;
  }

  /* ---- Magnetic CTA ---- */
  function initMagneticCta() {
    var btn = document.querySelector('.calibra-hero__cta');
    if (!btn || REDUCED) return;

    btn.addEventListener('mousemove', function (e) {
      var rect = btn.getBoundingClientRect();
      var cx = rect.left + rect.width / 2;
      var cy = rect.top + rect.height / 2;
      var dx = (e.clientX - cx) / rect.width;
      var dy = (e.clientY - cy) / rect.height;
      var strength = 8;
      btn.style.transform = 'translate(' + (dx * strength) + 'px, ' + (dy * strength) + 'px)';
    });

    btn.addEventListener('mouseleave', function () {
      btn.style.transform = '';
    });
  }

  /* ---- Hero bottle parallax ---- */
  function initBottleParallax() {
    var bottle = document.querySelector('.calibra-hero__bottle');
    if (!bottle || REDUCED) return;

    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var scrolled = window.scrollY;
          if (scrolled < window.innerHeight) {
            var rate = scrolled * 0.18;
            bottle.style.transform = 'translateY(' + (-rate) + 'px) rotate(-0.5deg)';
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  /* ---- Hero bottle image cycling ---- */
  function initHeroBottleCycle() {
    var bottle = document.querySelector('.calibra-hero__bottle');
    if (!bottle) return;

    /* Read bottles from data attribute set by section Liquid */
    var dataAttr = bottle.getAttribute('data-calibra-bottles');
    if (!dataAttr) return;

    var bottles;
    try { bottles = JSON.parse(dataAttr); } catch (e) { return; }
    if (!Array.isArray(bottles) || bottles.length < 2) return;

    /* Preload */
    bottles.forEach(function (src) { var img = new Image(); img.src = src; });

    var current = 0;
    setInterval(function () {
      current = (current + 1) % bottles.length;
      bottle.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
      bottle.style.opacity = '0';
      bottle.style.transform = 'translateY(10px)';
      setTimeout(function () {
        bottle.src = bottles[current];
        bottle.style.opacity = '1';
        bottle.style.transform = 'translateY(0)';
      }, 500);
    }, 4500);
  }

  /* ---- Flavour showcase: horizontal slider + blob colour morph ---- */
  function initShowcase() {
    var stage = document.querySelector('.calibra-showcase__stage');
    var track = document.querySelector('.calibra-showcase__track');
    if (!stage || !track) return;

    var panels = track.querySelectorAll('.calibra-showcase__panel');
    var dots   = stage.querySelectorAll('.calibra-showcase__dot');
    var arrows = stage.querySelectorAll('.calibra-showcase__arrow');
    var blob   = stage.querySelector('.calibra-showcase__blob');
    var N      = panels.length;
    var cur    = -1;

    function applyFlavour(idx) {
      if (idx === cur || idx < 0 || idx >= N) return;
      cur = idx;
      panels.forEach(function (p, i) { p.classList.toggle('calibra-panel-active', i === idx); });
      dots.forEach(function (d, i) { d.classList.toggle('calibra-dot-active', i === idx); });
      if (blob && FLAVOUR_BLOB[idx]) blob.style.background = FLAVOUR_BLOB[idx];
      arrows.forEach(function (a) {
        var dir = parseInt(a.getAttribute('data-calibra-dir'), 10);
        if (dir === -1) a.disabled = idx === 0;
        if (dir === 1) a.disabled = idx === N - 1;
      });
    }

    function goTo(idx, smooth) {
      idx = clamp(idx, 0, N - 1);
      track.scrollTo({ left: idx * track.clientWidth, behavior: (REDUCED || smooth === false) ? 'auto' : 'smooth' });
      applyFlavour(idx);
    }

    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = null;
        applyFlavour(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
      });
    }, { passive: true });

    arrows.forEach(function (a) {
      a.addEventListener('click', function () {
        goTo(cur + parseInt(a.getAttribute('data-calibra-dir'), 10));
      });
    });

    dots.forEach(function (d) {
      d.addEventListener('click', function () {
        goTo(parseInt(d.getAttribute('data-calibra-goto'), 10));
      });
    });

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { goTo(cur + 1); e.preventDefault(); }
      if (e.key === 'ArrowLeft') { goTo(cur - 1); e.preventDefault(); }
    });

    /* Ticker deep-links: any [data-calibra-flavour] scrolls here and selects */
    document.querySelectorAll('[data-calibra-flavour]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        var idx = parseInt(el.getAttribute('data-calibra-flavour'), 10);
        var section = document.getElementById('calibra-flavours');
        if (section) section.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
        window.setTimeout(function () { goTo(idx, !REDUCED); }, REDUCED ? 0 : 450);
      });
    });

    window.addEventListener('resize', function () { if (cur >= 0) goTo(cur, false); });

    applyFlavour(0);
  }

  /* ---- Waitlist form feedback (Shopify {% form 'customer' %} handles POST;
         this only provides optimistic UI on submit before redirect) ---- */
  function initWaitlistFeedback() {
    var form = document.querySelector('.calibra-waitlist__form');
    if (!form) return;

    /* If Shopify has already posted and shown success/error, bail */
    var successEl = document.querySelector('.calibra-waitlist__success');
    var errorEl   = document.querySelector('.calibra-waitlist__error');
    if (successEl && successEl.style.display === 'block') return;
    if (errorEl   && errorEl.style.display   === 'block') return;

    /* Visual loading state on submit (page will reload on response) */
    form.addEventListener('submit', function () {
      var btn = form.querySelector('.calibra-waitlist__btn');
      if (btn) {
        btn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10" stroke-width="2.5"/></svg>';
        btn.style.opacity = '0.7';
      }
    });
  }

  /* ---- Boot ---- */
  function boot() {
    initReveals();
    initTicker();
    initMagneticCta();
    initBottleParallax();
    initHeroBottleCycle();
    initShowcase();
    initWaitlistFeedback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
